import { XY } from "./xy";
import type { EntityClass } from "./levelgenerator/prefabs/entityRegistry";

type AnyClass = { prototype: any };

const thingRegistry = new Map<string, (data: any) => any>();
const weaponRegistry = new Map<string, (data: any, owner: any) => any>();
const thingClassRegistry = new Map<string, EntityClass>();
// Holds the class for every registered thing (including non-PhysicalThing entities
// like LevelDirector or traps). Used for hierarchy introspection (e.g. tests that
// want to filter registered things by base class or by isVisible()).
const allThingClassesRegistry = new Map<string, AnyClass>();

// Item registry — for CollectableItem subclasses
const itemRegistry = new Map<string, (data: any) => any>();

export function registerItem(tag: string, deserializer: (data: any) => any): void {
	itemRegistry.set(tag, deserializer);
}

export function deserializeItem(data: any): any {
	const fn = itemRegistry.get(data.type);
	if (!fn) throw new Error(`Unknown item type: ${data.type}`);
	return fn(data);
}

export function hasItem(tag: string): boolean {
	return itemRegistry.has(tag);
}

export function getItemRegistryKeys(): string[] {
	return [...itemRegistry.keys()];
}

// Weapon class registry — Map from weapon static key to constructor.
// Used by Ammo.pickupBy to instantiate weapons by string key.
type WeaponConstructor = new (owner: any) => any;
const weaponClassRegistry = new Map<string, WeaponConstructor>();

export function registerWeaponClass(tag: string, ctor: WeaponConstructor): void {
	weaponClassRegistry.set(tag, ctor);
}

export function getWeaponClass(tag: string): WeaponConstructor {
	const ctor = weaponClassRegistry.get(tag);
	if (!ctor) throw new Error(`Unknown weapon class: ${tag}`);
	return ctor;
}

export function hasWeaponClass(tag: string): boolean {
	return weaponClassRegistry.has(tag);
}

export function getWeaponClassRegistryKeys(): string[] {
	return [...weaponClassRegistry.keys()];
}

export function registerThing(tag: string, deserializer: (data: any) => any, cls?: AnyClass): void {
	thingRegistry.set(tag, deserializer);
	if (cls) allThingClassesRegistry.set(tag, cls);
}

export function getRegisteredThingClass(tag: string): AnyClass | undefined {
	return allThingClassesRegistry.get(tag);
}

export function registerWeapon(tag: string, deserializer: (data: any, owner: any) => any): void {
	weaponRegistry.set(tag, deserializer);
}

export function serializeThing(thing: { serialize(): any }): any {
	return thing.serialize();
}

export function deserializeThing(data: any): any {
	const fn = thingRegistry.get(data.type);
	if (!fn) throw new Error(`Unknown thing type: ${data.type}`);
	return fn(data);
}

export function deserializeWeapon(data: any, owner: any): any {
	const fn = weaponRegistry.get(data.type);
	if (!fn) throw new Error(`Unknown weapon type: ${data.type}`);
	return fn(data, owner);
}

export function getThingRegistryKeys(): string[] {
	return [...thingRegistry.keys()];
}

export function getWeaponRegistryKeys(): string[] {
	return [...weaponRegistry.keys()];
}

export function registerThingClass(tag: string, cls: EntityClass): void {
	thingClassRegistry.set(tag, cls);
}

export function getThingClass(tag: string): EntityClass | undefined {
	return thingClassRegistry.get(tag);
}

export function getThingClassKeys(): string[] {
	return [...thingClassRegistry.keys()];
}

const mobFactoryRegistry = new Map<string, () => any>();

export function registerMobFactory(type: string, factory: () => any): void {
	mobFactoryRegistry.set(type, factory);
}

export function getMobFactory(type: string): () => any {
	const factory = mobFactoryRegistry.get(type);
	if (!factory) throw new Error(`Unknown mob factory type: ${type}`);
	return factory;
}

export function serializeXY(xy: XY): { x: number; y: number } {
	return { x: xy.x, y: xy.y };
}

export function deserializeXY(data: { x: number; y: number }): XY {
	return new XY(data.x, data.y);
}
